// Language Context for trilingual support (EN/PT/ES)
import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Language names for selector
    languageName: 'English',
    languageCode: 'EN',
    // Navbar
    nav: {
      home: 'Home',
      catalog: 'Peptides',
      coa: 'COA Library',
      pricing: 'Pricing',
      contact: 'Contact',
    },
    // Hero
    hero: {
      title: 'Premium Research Peptides',
      subtitle: 'High-purity peptides for scientific research and development. Export only.',
      cta: 'View Catalog',
      learnMore: 'Learn More',
    },
    // Disclaimer
    disclaimer: {
      title: 'Research Use Only',
      subtitle: 'PEP-T.COM provides materials strictly for scientific laboratory research. To continue, you must acknowledge the following:',
      ageRequirement: 'Age Requirement',
      ageText: 'All customers must be at least 21 years of age.',
      productUse: 'Product Use',
      productUseText: 'All products are sold for in-vitro research applications only. They are not for human or animal consumption, cosmetic use, or as dietary supplements.',
      noMedicalAdvice: 'No Medical Advice',
      noMedicalAdviceText: 'We are not a pharmacy or medical provider. We do not provide medical advice, dosing instructions, or reconstitution guidance for human use.',
      compliance: 'Compliance',
      complianceText: "It is the researcher's responsibility to handle all materials in accordance with their institution's safety protocols and all applicable local, state, and federal laws.",
      salesFinal: 'Sales Are Final',
      salesFinalText: 'Due to the nature of these materials, all sales are final. We do not accept returns or exchanges.',
      exportOnly: 'Export Only',
      exportOnlyText: 'Products are sold for export only. Not available for domestic sale in the United States.',
      agree: 'I Agree',
      disagree: 'I Do Not Agree',
      confirmText: 'By clicking "I Agree", you confirm you have read and agree to our Terms & Conditions and Disclaimer.',
    },
    // Categories
    categories: {
      title: 'Browse by Category',
      viewAll: 'View All',
    },
    // Products
    products: {
      featured: 'Featured Peptides',
      viewDetails: 'View Details',
      inStock: 'On Order',
      outOfStock: 'Out of Stock',
      purity: 'Purity',
      searchPlaceholder: 'Search peptides by name, category, or catalog number...',
      noResults: 'No peptides found matching your search.',
      allCategories: 'All Categories',
      productsAvailable: 'products available',
      productAvailable: 'product available',
      clear: 'Clear',
      clearFilters: 'Clear Filters',
      backToCatalog: 'Back to Catalog',
      form: 'Form',
      lyophilizedPowder: 'Lyophilized Powder',
      trending: 'Trending',
    },
    // Product Detail
    productDetail: {
      specifications: 'Specifications',
      description: 'Description',
      molecularFormula: 'Molecular Formula',
      molecularWeight: 'Molecular Weight',
      casNumber: 'CAS Number',
      sequence: 'Sequence',
      storage: 'Storage',
      catalogNumber: 'Catalog No.',
      concentration: 'Concentration',
      category: 'Category',
      downloadCoa: 'Download COA',
      requestQuote: 'Request Quote',
      researchOnly: 'For Research Use Only',
    },
    // COA
    coa: {
      title: 'Certificate of Analysis Library',
      subtitle: 'Search and verify product quality certificates by batch ID, product name, or lot number.',
      searchPlaceholder: 'Enter batch ID, product name, or lot number...',
      batchId: 'Batch ID',
      productName: 'Product Name',
      lotNumber: 'Lot Number',
      testDate: 'Test Date',
      purity: 'Purity',
      appearance: 'Appearance',
      solubility: 'Solubility',
      sterility: 'Sterility',
      massSpec: 'Mass Spec',
      endotoxin: 'Endotoxin',
      noResults: 'No COA records found. Try a different search term.',
      viewDetails: 'View Details',
      actions: 'Actions',
      certificateTitle: 'Certificate of Analysis',
      close: 'Close',
    },
    // Contact
    contact: {
      title: 'Contact Us',
      subtitle: 'Have questions about our products or services? Get in touch with our team.',
      name: 'Full Name',
      email: 'Email Address',
      company: 'Company/Institution (Optional)',
      country: 'Country',
      subject: 'Subject',
      message: 'Message',
      send: 'Send Message',
      success: 'Message sent successfully! We will contact you shortly.',
      error: 'Failed to send message. Please try again.',
      emailLabel: 'Email',
      emailValue: 'support@pep-t.com',
      locationLabel: 'Location',
      locationValue: 'Boca Raton, Florida, USA',
      selectCountry: 'Select a country',
      sending: 'Sending...',
      notice: 'Notice',
      exportNotice: 'Our products are for export only. We do not sell to the US domestic market.',
    },
    // Footer
    footer: {
      description: 'Premium research peptides for scientific development. Export only.',
      quickLinks: 'Quick Links',
      legal: 'Legal',
      terms: 'Terms & Conditions',
      privacy: 'Privacy Policy',
      disclaimer: 'Disclaimer',
      exportNotice: 'For Export Only - Not for US Domestic Sale',
      copyright: '© 2024 PEP-T.COM. All rights reserved.',
      researchOnly: 'Products are for in-vitro research use only.',
    },
    // WhatsApp
    whatsapp: {
      chat: 'Chat with Support',
    },
    // Quality Section
    quality: {
      title: 'Scientific Excellence',
      subtitle: 'Every peptide undergoes rigorous quality control',
      purity: '99%+ Purity',
      purityText: 'HPLC and Mass Spectrometry verified',
      coa: 'Full COA',
      coaText: 'Certificate of Analysis for every batch',
      usa: 'Tested in USA',
      usaText: 'Quality tested in United States laboratories',
      export: 'Global Export',
      exportText: 'Worldwide shipping for research institutions',
      hplcAnalysis: 'HPLC & Mass Spectrometry Analysis',
      coaEveryBatch: 'Certificate of Analysis (COA) for Every Batch',
      strictQuality: 'Strict Quality Control Standards',
      lyophilized: 'Lyophilized for Optimal Stability',
      viewCoaLibrary: 'View COA Library',
      researchOnly: 'Research Use Only',
      trendingPeptides: 'Trending Peptides',
    },
  },
  pt: {
    // Language names for selector
    languageName: 'Português',
    languageCode: 'PT',
    // Navbar
    nav: {
      home: 'Início',
      catalog: 'Peptídeos',
      coa: 'Biblioteca COA',
      pricing: 'Preços',
      contact: 'Contato',
    },
    // Hero
    hero: {
      title: 'Peptídeos de Pesquisa Premium',
      subtitle: 'Peptídeos de alta pureza para pesquisa e desenvolvimento científico. Apenas exportação.',
      cta: 'Ver Catálogo',
      learnMore: 'Saiba Mais',
    },
    // Disclaimer
    disclaimer: {
      title: 'Apenas para Uso em Pesquisa',
      subtitle: 'PEP-T.COM fornece materiais estritamente para pesquisa laboratorial científica. Para continuar, você deve reconhecer o seguinte:',
      ageRequirement: 'Requisito de Idade',
      ageText: 'Todos os clientes devem ter pelo menos 21 anos de idade.',
      productUse: 'Uso do Produto',
      productUseText: 'Todos os produtos são vendidos apenas para aplicações de pesquisa in-vitro. Não são para consumo humano ou animal, uso cosmético ou como suplementos alimentares.',
      noMedicalAdvice: 'Sem Aconselhamento Médico',
      noMedicalAdviceText: 'Não somos uma farmácia ou provedor médico. Não fornecemos aconselhamento médico, instruções de dosagem ou orientação de reconstituição para uso humano.',
      compliance: 'Conformidade',
      complianceText: 'É responsabilidade do pesquisador manusear todos os materiais de acordo com os protocolos de segurança de sua instituição e todas as leis locais, estaduais e federais aplicáveis.',
      salesFinal: 'Vendas São Finais',
      salesFinalText: 'Devido à natureza desses materiais, todas as vendas são finais. Não aceitamos devoluções ou trocas.',
      exportOnly: 'Apenas Exportação',
      exportOnlyText: 'Os produtos são vendidos apenas para exportação. Não disponível para venda doméstica nos Estados Unidos.',
      agree: 'Eu Concordo',
      disagree: 'Eu Não Concordo',
      confirmText: 'Ao clicar em "Eu Concordo", você confirma que leu e concorda com nossos Termos e Condições e Aviso Legal.',
    },
    // Categories
    categories: {
      title: 'Navegar por Categoria',
      viewAll: 'Ver Todos',
    },
    // Products
    products: {
      featured: 'Peptídeos em Destaque',
      viewDetails: 'Ver Detalhes',
      inStock: 'Sob Pedido',
      outOfStock: 'Fora de Estoque',
      purity: 'Pureza',
      searchPlaceholder: 'Pesquisar peptídeos por nome, categoria ou número de catálogo...',
      noResults: 'Nenhum peptídeo encontrado para sua pesquisa.',
      allCategories: 'Todas as Categorias',
      productsAvailable: 'produtos disponíveis',
      productAvailable: 'produto disponível',
      clear: 'Limpar',
      clearFilters: 'Limpar Filtros',
      backToCatalog: 'Voltar ao Catálogo',
      form: 'Forma',
      lyophilizedPowder: 'Pó Liofilizado',
      trending: 'Em Alta',
    },
    // Product Detail
    productDetail: {
      specifications: 'Especificações',
      description: 'Descrição',
      molecularFormula: 'Fórmula Molecular',
      molecularWeight: 'Peso Molecular',
      casNumber: 'Número CAS',
      sequence: 'Sequência',
      storage: 'Armazenamento',
      catalogNumber: 'Nº Catálogo',
      concentration: 'Concentração',
      category: 'Categoria',
      downloadCoa: 'Baixar COA',
      requestQuote: 'Solicitar Cotação',
      researchOnly: 'Apenas para Uso em Pesquisa',
    },
    // COA
    coa: {
      title: 'Biblioteca de Certificados de Análise',
      subtitle: 'Pesquise e verifique certificados de qualidade por ID de lote, nome do produto ou número do lote.',
      searchPlaceholder: 'Digite ID do lote, nome do produto ou número do lote...',
      batchId: 'ID do Lote',
      productName: 'Nome do Produto',
      lotNumber: 'Número do Lote',
      testDate: 'Data do Teste',
      purity: 'Pureza',
      appearance: 'Aparência',
      solubility: 'Solubilidade',
      sterility: 'Esterilidade',
      massSpec: 'Espectrometria de Massa',
      endotoxin: 'Endotoxina',
      noResults: 'Nenhum registro COA encontrado. Tente um termo de pesquisa diferente.',
      viewDetails: 'Ver Detalhes',
      actions: 'Ações',
      certificateTitle: 'Certificado de Análise',
      close: 'Fechar',
    },
    // Contact
    contact: {
      title: 'Entre em Contato',
      subtitle: 'Tem dúvidas sobre nossos produtos ou serviços? Entre em contato com nossa equipe.',
      name: 'Nome Completo',
      email: 'Endereço de Email',
      company: 'Empresa/Instituição (Opcional)',
      country: 'País',
      subject: 'Assunto',
      message: 'Mensagem',
      send: 'Enviar Mensagem',
      success: 'Mensagem enviada com sucesso! Entraremos em contato em breve.',
      error: 'Falha ao enviar mensagem. Por favor, tente novamente.',
      emailLabel: 'Email',
      emailValue: 'support@pep-t.com',
      locationLabel: 'Localização',
      locationValue: 'Boca Raton, Flórida, EUA',
      selectCountry: 'Selecione um país',
      sending: 'Enviando...',
      notice: 'Aviso',
      exportNotice: 'Nossos produtos são destinados apenas para exportação. Não vendemos para o mercado doméstico dos EUA.',
    },
    // Footer
    footer: {
      description: 'Peptídeos de pesquisa premium para desenvolvimento científico. Apenas exportação.',
      quickLinks: 'Links Rápidos',
      legal: 'Legal',
      terms: 'Termos e Condições',
      privacy: 'Política de Privacidade',
      disclaimer: 'Aviso Legal',
      exportNotice: 'Apenas para Exportação - Não para Venda Doméstica nos EUA',
      copyright: '© 2024 PEP-T.COM. Todos os direitos reservados.',
      researchOnly: 'Os produtos são apenas para uso em pesquisa in-vitro.',
    },
    // WhatsApp
    whatsapp: {
      chat: 'Falar com Suporte',
    },
    // Quality Section
    quality: {
      title: 'Excelência Científica',
      subtitle: 'Cada peptídeo passa por rigoroso controle de qualidade',
      purity: '99%+ Pureza',
      purityText: 'Verificado por HPLC e Espectrometria de Massa',
      coa: 'COA Completo',
      coaText: 'Certificado de Análise para cada lote',
      usa: 'Testado nos EUA',
      usaText: 'Qualidade testada em laboratórios dos Estados Unidos',
      export: 'Exportação Global',
      exportText: 'Envio mundial para instituições de pesquisa',
      hplcAnalysis: 'Análise por HPLC e Espectrometria de Massa',
      coaEveryBatch: 'Certificado de Análise (COA) para Cada Lote',
      strictQuality: 'Padrões Rigorosos de Controle de Qualidade',
      lyophilized: 'Liofilizado para Estabilidade Ideal',
      viewCoaLibrary: 'Ver Biblioteca COA',
      researchOnly: 'Apenas para Pesquisa',
      trendingPeptides: 'Peptídeos em Alta',
    },
  },
  es: {
    // Language names for selector
    languageName: 'Español',
    languageCode: 'ES',
    // Navbar
    nav: {
      home: 'Inicio',
      catalog: 'Péptidos',
      coa: 'Biblioteca COA',
      pricing: 'Precios',
      contact: 'Contacto',
    },
    // Hero
    hero: {
      title: 'Péptidos de Investigación Premium',
      subtitle: 'Péptidos de alta pureza para investigación y desarrollo científico. Solo exportación.',
      cta: 'Ver Catálogo',
      learnMore: 'Más Información',
    },
    // Disclaimer
    disclaimer: {
      title: 'Solo para Uso en Investigación',
      subtitle: 'PEP-T.COM proporciona materiales estrictamente para investigación científica de laboratorio. Para continuar, debe reconocer lo siguiente:',
      ageRequirement: 'Requisito de Edad',
      ageText: 'Todos los clientes deben tener al menos 21 años de edad.',
      productUse: 'Uso del Producto',
      productUseText: 'Todos los productos se venden únicamente para aplicaciones de investigación in-vitro. No son para consumo humano o animal, uso cosmético o como suplementos dietéticos.',
      noMedicalAdvice: 'Sin Asesoramiento Médico',
      noMedicalAdviceText: 'No somos una farmacia ni proveedor médico. No proporcionamos asesoramiento médico, instrucciones de dosificación ni orientación de reconstitución para uso humano.',
      compliance: 'Cumplimiento',
      complianceText: 'Es responsabilidad del investigador manejar todos los materiales de acuerdo con los protocolos de seguridad de su institución y todas las leyes locales, estatales y federales aplicables.',
      salesFinal: 'Ventas Finales',
      salesFinalText: 'Debido a la naturaleza de estos materiales, todas las ventas son finales. No aceptamos devoluciones ni cambios.',
      exportOnly: 'Solo Exportación',
      exportOnlyText: 'Los productos se venden solo para exportación. No disponible para venta doméstica en Estados Unidos.',
      agree: 'Acepto',
      disagree: 'No Acepto',
      confirmText: 'Al hacer clic en "Acepto", confirma que ha leído y acepta nuestros Términos y Condiciones y Aviso Legal.',
    },
    // Categories
    categories: {
      title: 'Explorar por Categoría',
      viewAll: 'Ver Todos',
    },
    // Products
    products: {
      featured: 'Péptidos Destacados',
      viewDetails: 'Ver Detalles',
      inStock: 'Bajo Pedido',
      outOfStock: 'Agotado',
      purity: 'Pureza',
      searchPlaceholder: 'Buscar péptidos por nombre, categoría o número de catálogo...',
      noResults: 'No se encontraron péptidos que coincidan con su búsqueda.',
      allCategories: 'Todas las Categorías',
      productsAvailable: 'productos disponibles',
      productAvailable: 'producto disponible',
      clear: 'Limpiar',
      clearFilters: 'Limpiar Filtros',
      backToCatalog: 'Volver al Catálogo',
      form: 'Forma',
      lyophilizedPowder: 'Polvo Liofilizado',
      trending: 'Tendencia',
    },
    // Product Detail
    productDetail: {
      specifications: 'Especificaciones',
      description: 'Descripción',
      molecularFormula: 'Fórmula Molecular',
      molecularWeight: 'Peso Molecular',
      casNumber: 'Número CAS',
      sequence: 'Secuencia',
      storage: 'Almacenamiento',
      catalogNumber: 'Nº Catálogo',
      concentration: 'Concentración',
      category: 'Categoría',
      downloadCoa: 'Descargar COA',
      requestQuote: 'Solicitar Cotización',
      researchOnly: 'Solo para Uso en Investigación',
    },
    // COA
    coa: {
      title: 'Biblioteca de Certificados de Análisis',
      subtitle: 'Busque y verifique certificados de calidad por ID de lote, nombre del producto o número de lote.',
      searchPlaceholder: 'Ingrese ID de lote, nombre del producto o número de lote...',
      batchId: 'ID de Lote',
      productName: 'Nombre del Producto',
      lotNumber: 'Número de Lote',
      testDate: 'Fecha de Prueba',
      purity: 'Pureza',
      appearance: 'Apariencia',
      solubility: 'Solubilidad',
      sterility: 'Esterilidad',
      massSpec: 'Espectrometría de Masas',
      endotoxin: 'Endotoxina',
      noResults: 'No se encontraron registros COA. Intente con un término de búsqueda diferente.',
      viewDetails: 'Ver Detalles',
      actions: 'Acciones',
      certificateTitle: 'Certificado de Análisis',
      close: 'Cerrar',
    },
    // Contact
    contact: {
      title: 'Contáctenos',
      subtitle: '¿Tiene preguntas sobre nuestros productos o servicios? Póngase en contacto con nuestro equipo.',
      name: 'Nombre Completo',
      email: 'Dirección de Email',
      company: 'Empresa/Institución (Opcional)',
      country: 'País',
      subject: 'Asunto',
      message: 'Mensaje',
      send: 'Enviar Mensaje',
      success: '¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.',
      error: 'Error al enviar mensaje. Por favor, inténtelo de nuevo.',
      emailLabel: 'Email',
      emailValue: 'support@pep-t.com',
      locationLabel: 'Ubicación',
      locationValue: 'Boca Raton, Florida, EE.UU.',
      selectCountry: 'Seleccione un país',
      sending: 'Enviando...',
      notice: 'Aviso',
      exportNotice: 'Nuestros productos son solo para exportación. No vendemos al mercado doméstico de EE.UU.',
    },
    // Footer
    footer: {
      description: 'Péptidos de investigación premium para desarrollo científico. Solo exportación.',
      quickLinks: 'Enlaces Rápidos',
      legal: 'Legal',
      terms: 'Términos y Condiciones',
      privacy: 'Política de Privacidad',
      disclaimer: 'Aviso Legal',
      exportNotice: 'Solo para Exportación - No para Venta Doméstica en EE.UU.',
      copyright: '© 2024 PEP-T.COM. Todos los derechos reservados.',
      researchOnly: 'Los productos son solo para uso en investigación in-vitro.',
    },
    // WhatsApp
    whatsapp: {
      chat: 'Chatear con Soporte',
    },
    // Quality Section
    quality: {
      title: 'Excelencia Científica',
      subtitle: 'Cada péptido pasa por riguroso control de calidad',
      purity: '99%+ Pureza',
      purityText: 'Verificado por HPLC y Espectrometría de Masas',
      coa: 'COA Completo',
      coaText: 'Certificado de Análisis para cada lote',
      usa: 'Probado en EE.UU.',
      usaText: 'Calidad probada en laboratorios de Estados Unidos',
      export: 'Exportación Global',
      exportText: 'Envío mundial para instituciones de investigación',
      hplcAnalysis: 'Análisis por HPLC y Espectrometría de Masas',
      coaEveryBatch: 'Certificado de Análisis (COA) para Cada Lote',
      strictQuality: 'Estándares Rigurosos de Control de Calidad',
      lyophilized: 'Liofilizado para Estabilidad Óptima',
      viewCoaLibrary: 'Ver Biblioteca COA',
      researchOnly: 'Solo para Investigación',
      trendingPeptides: 'Péptidos en Tendencia',
    },
  },
};

// Country codes for language detection
// Brazil only -> Portuguese
const PORTUGUESE_COUNTRIES = ['BR'];

// Spanish-speaking Latin America and Central America
const SPANISH_COUNTRIES = [
  'AR', // Argentina
  'BO', // Bolivia
  'CL', // Chile
  'CO', // Colombia
  'CR', // Costa Rica
  'CU', // Cuba
  'DO', // Dominican Republic
  'EC', // Ecuador
  'SV', // El Salvador
  'GT', // Guatemala
  'HN', // Honduras
  'MX', // Mexico
  'NI', // Nicaragua
  'PA', // Panama
  'PY', // Paraguay
  'PE', // Peru
  'PR', // Puerto Rico
  'UY', // Uruguay
  'VE', // Venezuela
  'ES', // Spain
];

// Detect user's country via IP geolocation
const detectUserCountry = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      return data.country_code;
    }
  } catch (error) {
    console.log('Could not detect country, defaulting to EN');
  }
  return null;
};

// Determine language based on country
const getLanguageFromCountry = (countryCode) => {
  if (!countryCode) return 'en';
  if (PORTUGUESE_COUNTRIES.includes(countryCode)) return 'pt';
  if (SPANISH_COUNTRIES.includes(countryCode)) return 'es';
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeLanguage = async () => {
      // First check if user has a saved preference
      const savedLang = localStorage.getItem('pept-language');
      if (savedLang && ['en', 'pt', 'es'].includes(savedLang)) {
        setLanguage(savedLang);
        setIsInitialized(true);
        return;
      }
      
      // No saved preference, detect by country
      const countryCode = await detectUserCountry();
      const detectedLang = getLanguageFromCountry(countryCode);
      setLanguage(detectedLang);
      localStorage.setItem('pept-language', detectedLang);
      setIsInitialized(true);
    };
    
    initializeLanguage();
  }, []);

  // Change language function (replaces toggle)
  const changeLanguage = (newLang) => {
    if (['en', 'pt', 'es'].includes(newLang)) {
      setLanguage(newLang);
      localStorage.setItem('pept-language', newLang);
    }
  };

  // Legacy toggle (cycles through languages)
  const toggleLanguage = () => {
    const langs = ['en', 'pt', 'es'];
    const currentIndex = langs.indexOf(language);
    const nextIndex = (currentIndex + 1) % langs.length;
    changeLanguage(langs[nextIndex]);
  };

  const t = (path) => {
    const keys = path.split('.');
    let result = translations[language];
    for (const key of keys) {
      result = result?.[key];
    }
    return result || path;
  };

  // Available languages for selector
  const availableLanguages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ];

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      toggleLanguage, 
      t, 
      translations: translations[language], 
      isInitialized,
      availableLanguages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
